import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Edit, User, Mail, Phone, Calendar, MapPin, Shield, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  hire_date: string;
  is_active: boolean;
  role: 'staff' | 'admin';
}

interface EmployeeProfile {
  hourly_rate: number | null;
  salary_type: string | null;
  overtime_rate: number | null;
  payment_method: string | null;
  avatar_url: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relation: string | null;
  medical_notes: string | null;
}

interface EmployeeOverviewProps {
  employee: Employee;
  profile: EmployeeProfile | null;
  onDataUpdate: () => void;
}

export const EmployeeOverview: React.FC<EmployeeOverviewProps> = ({
  employee,
  profile,
  onDataUpdate
}) => {
  const [editingEmployee, setEditingEmployee] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [loading, setLoading] = useState(false);

  const [employeeForm, setEmployeeForm] = useState({
    first_name: employee.first_name,
    last_name: employee.last_name,
    email: employee.email || '',
    phone: employee.phone || '',
    hire_date: employee.hire_date,
    role: employee.role
  });

  const [profileForm, setProfileForm] = useState({
    hourly_rate: profile?.hourly_rate?.toString() || '',
    overtime_rate: profile?.overtime_rate?.toString() || '',
    salary_type: profile?.salary_type || 'hourly',
    payment_method: profile?.payment_method || 'direct_deposit'
  });

  const handleUpdateEmployee = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('employees')
        .update({
          first_name: employeeForm.first_name,
          last_name: employeeForm.last_name,
          email: employeeForm.email || null,
          phone: employeeForm.phone || null,
          hire_date: employeeForm.hire_date,
          role: employeeForm.role
        })
        .eq('id', employee.id);

      if (error) throw error;

      toast.success('Employee information updated successfully');
      setEditingEmployee(false);
      onDataUpdate();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error('Failed to update employee information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      const updates = {
        hourly_rate: profileForm.hourly_rate ? parseFloat(profileForm.hourly_rate) : null,
        overtime_rate: profileForm.overtime_rate ? parseFloat(profileForm.overtime_rate) : null,
        salary_type: profileForm.salary_type,
        payment_method: profileForm.payment_method
      };

      if (profile) {
        // Update existing profile
        const { error } = await supabase
          .from('employee_profiles')
          .update(updates)
          .eq('employee_id', employee.id);

        if (error) throw error;
      } else {
        // Create new profile
        const { error } = await supabase
          .from('employee_profiles')
          .insert({
            employee_id: employee.id,
            ...updates
          });

        if (error) throw error;
      }

      toast.success('Profile settings updated successfully');
      setEditingProfile(false);
      onDataUpdate();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Personal Information */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditingEmployee(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">First Name</Label>
              <p className="font-medium">{employee.first_name}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Last Name</Label>
              <p className="font-medium">{employee.last_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label className="text-sm text-muted-foreground">Email</Label>
              <p className="font-medium">{employee.email || 'Not provided'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label className="text-sm text-muted-foreground">Phone</Label>
              <p className="font-medium">{employee.phone || 'Not provided'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label className="text-sm text-muted-foreground">Hire Date</Label>
              <p className="font-medium">{format(new Date(employee.hire_date), 'MMMM dd, yyyy')}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label className="text-sm text-muted-foreground">Role</Label>
              <p className="font-medium capitalize">{employee.role}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payroll Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Payroll Settings
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditingProfile(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">Hourly Rate</Label>
              <p className="font-medium text-lg">
                ${profile?.hourly_rate?.toFixed(2) || 'Not set'}
              </p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Overtime Rate</Label>
              <p className="font-medium text-lg">
                ${profile?.overtime_rate?.toFixed(2) || 'Not set'}
              </p>
            </div>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground">Salary Type</Label>
            <p className="font-medium capitalize">{profile?.salary_type || 'Not set'}</p>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground">Payment Method</Label>
            <p className="font-medium capitalize">
              {profile?.payment_method?.replace('_', ' ') || 'Not set'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Emergency Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {profile?.emergency_contact_name ? (
            <>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label className="text-sm text-muted-foreground">Name</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{profile.emergency_contact_name}</p>
                    {profile.emergency_contact_relation && (
                      <Badge variant="outline" className="text-xs">
                        {profile.emergency_contact_relation}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {profile.emergency_contact_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <Label className="text-sm text-muted-foreground">Phone</Label>
                    <p className="font-medium">{profile.emergency_contact_phone}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">No emergency contact information available</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Employee Dialog */}
      <Dialog open={editingEmployee} onOpenChange={setEditingEmployee}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Employee Information</DialogTitle>
            <DialogDescription>
              Update the employee's personal details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="hire_date">Hire Date</Label>
              <Input
                id="hire_date"
                type="date"
                value={employeeForm.hire_date}
                onChange={(e) => setEmployeeForm(prev => ({ ...prev, hire_date: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select value={employeeForm.role} onValueChange={(value: 'staff' | 'admin') => setEmployeeForm(prev => ({ ...prev, role: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEmployee(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateEmployee} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={editingProfile} onOpenChange={setEditingProfile}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payroll Settings</DialogTitle>
            <DialogDescription>
              Update the employee's wage and payment information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  value={profileForm.hourly_rate}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, hourly_rate: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="overtime_rate">Overtime Rate ($)</Label>
                <Input
                  id="overtime_rate"
                  type="number"
                  step="0.01"
                  value={profileForm.overtime_rate}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, overtime_rate: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="salary_type">Salary Type</Label>
              <Select value={profileForm.salary_type} onValueChange={(value) => setProfileForm(prev => ({ ...prev, salary_type: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="salary">Salary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select value={profileForm.payment_method} onValueChange={(value) => setProfileForm(prev => ({ ...prev, payment_method: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProfile(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProfile} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};